import CourseService from '../components/course/course.service';
import SingleCourseCtrl from '../course/single/single-course.controller';
import singleCourseTemplate from '../course/single/single-course.html';
import UserAuthenticator from '../authentication/user-authenticator';
import UserResolver from '../authentication/user-resolver';
import CourseTrackService from '../components/course/course-track.service';

function buildCourseTitle(courseTrack: any): string {
  if (!courseTrack) return 'uTest Academy';
  if (courseTrack.user_is_locked_by_learning_track_order) return `uTest Academy Course - ${courseTrack.name}: Locked`;
  const courseName = courseTrack.current_course?.title || '';
  return courseName
    ? `uTest Academy Course - ${courseTrack.name}: ${courseName}`
    : `uTest Academy Course - ${courseTrack.name}`;
}

function buildTrackTitle(courseTrack: any, suffix: string): string {
  if (!courseTrack) return 'uTest Academy';
  return courseTrack.user_is_locked_by_learning_track_order
    ? `uTest Academy Course - ${courseTrack.name}: Locked`
    : `uTest Academy Course - ${courseTrack.name}: ${suffix}`;
}

function redirectToLearningTrackCourse($state: any, courseTrack: any, lessonId?: string): void {
  $state.go('learning-track-course', {
    learningTrackId: courseTrack.learning_track_id,
    courseTrackId: courseTrack.id,
    ...(lessonId ? { lessonId } : {}),
    preloadedCourseTrack: courseTrack,
  });
}

function redirectToLearningTrackQuiz($state: any, stateName: string, courseTrack: any): void {
  $state.go(stateName, {
    learningTrackId: courseTrack.learning_track_id,
    courseTrackId: courseTrack.id,
  }, { location: 'replace' });
}

const CoursesRouter = {
  route: (module) => {
    module
      .service('CourseService', CourseService)
      .controller('SingleCourseCtrl', SingleCourseCtrl)
      .service('CourseTrackService', CourseTrackService);

    module.run(['$transitions', ($transitions) => {
      // Reset the shared scroll container to the top when entering any
      // academy state
      $transitions.onSuccess({ to: (state) => !!state.includes?.academy }, () => {
        document.getElementById('main-content-container')?.scrollTo({ top: 0 });
      });
    }]);

    module.config(['$stateProvider', ($stateProvider) => {
      $stateProvider
        .state('courses', {
          url: '/courses?track',
          controller: ['$state', '$scope', function ($state, $scope) { $scope.$state = $state; }],
          abstract: true,
          template: '<div ui-view="courses-index" ng-show="$state.includes(\'course\')"></div><div ui-view="courses-show" ng-show="$state.includes(\'singlecourse\')"></div>',
          parent: 'root',
        })
        .state('academy', {
          url: '/academy',
          abstract: true,
          parent: 'root',
        })
        .state('academy-home', {
          url: '',
          component: 'academyHome',
          parent: 'academy',
          redirectTo: (trans) => trans.injector().getAsync('features').then(() => (
            trans.injector().get('featureFlags').isOn('UTEST_ACADEMY_REDESIGN_CERTIFICATIONS')
              ? 'academy-catalog'
              : undefined
          )),
          resolve: {
            title: [() => 'uTest Academy'],
            tracks: ['CourseTrackService', 'useRedesignCatalog', function (CourseTrackService, useRedesignCatalog) {
              if (useRedesignCatalog) return [];
              return CourseTrackService.all().then((tracks) => tracks.data);
            }],
            useRedesignCatalog: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG');
            }],
          },
        })
        .state('academy-catalog', {
          url: '/catalog',
          parent: 'academy',
          template: '<ut-academy-catalog></ut-academy-catalog>',
          resolve: {
            title: [() => 'uTest Academy'],
            user: UserAuthenticator,
          },
        })
        .state('academy-certifications', {
          url: '/certifications',
          parent: 'academy',
          template: '<ut-academy-certifications></ut-academy-certifications>',
          redirectTo: (trans) => trans.injector().getAsync('features').then(() => (
            trans.injector().get('featureFlags').isOn('UTEST_ACADEMY_REDESIGN_CERTIFICATIONS')
              ? undefined
              : 'academy-home'
          )),
          resolve: {
            title: [() => 'uTest Academy - Certifications'],
            user: UserAuthenticator,
          },
        })
        .state('academy-certification-detail', {
          url: '/certifications/:certificationId',
          parent: 'academy',
          template: '<ut-certification-detail></ut-certification-detail>',
          redirectTo: (trans) => trans.injector().getAsync('features').then(() => (
            trans.injector().get('featureFlags').isOn('UTEST_ACADEMY_REDESIGN_CERTIFICATIONS')
              ? undefined
              : 'academy-home'
          )),
          resolve: {
            title: [() => 'uTest Academy - Certification Details'],
            user: UserAuthenticator,
          },
        })
        .state('learning-track-course', {
          url: '/tracks/:learningTrackId/courses/:courseTrackId/lessons/:lessonId',
          parent: 'academy',
          component: 'academyCourseContainer',
          params: {
            lessonId: { squash: true, value: null },
            preloadedCourseTrack: { value: null, dynamic: true },
          },
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', '$state', function (CourseTrackService, $transition$, $state) {
              const params = $transition$.params();
              if (params.preloadedCourseTrack) return params.preloadedCourseTrack;
              const promise = params.lessonId
                ? CourseTrackService.trackWithCourse(params.courseTrackId, params.lessonId)
                : CourseTrackService.track(params.courseTrackId);
              return promise.then(res => res.data, () => {
                $state.go('home');
                return null;
              });
            }],
            user: UserResolver,
            useRedesign: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE');
            }],
            useRedesignCatalog: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG');
            }],
            title: ['courseTrack', buildCourseTitle],
          },
        })
        .state('track-course', {
          url: '/tracks/:trackId/courses/:courseId',
          parent: 'academy',
          component: 'academyCourseContainer',
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', '$state', 'featureFlags', function (CourseTrackService, $transition$, $state, featureFlags) {
              return CourseTrackService.trackWithCourse($transition$.params().trackId, $transition$.params().courseId).then(res => {
                const courseTrack = res.data;
                if (featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG') && courseTrack.learning_track_id) {
                  redirectToLearningTrackCourse($state, courseTrack, $transition$.params().courseId);
                  return null;
                }
                return courseTrack;
              }, () => {
                $state.go('home');
                return null;
              });
            }],
            user: UserResolver,
            useRedesign: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE');
            }],
            useRedesignCatalog: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG');
            }],
            title: ['courseTrack', buildCourseTitle],
          },
        })
        .state('tracks', {
          url: '/tracks/:trackId',
          parent: 'academy',
          component: 'academyCourseContainer',
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', '$state', 'featureFlags', function (CourseTrackService, $transition$, $state, featureFlags) {
              return CourseTrackService.track($transition$.params().trackId).then(res => {
                const courseTrack = res.data;
                if (featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG') && courseTrack.learning_track_id) {
                  redirectToLearningTrackCourse($state, courseTrack);
                  return null;
                }
                return courseTrack;
              }, () => {
                $state.go('home');
                return null;
              });
            }],
            user: UserResolver,
            useRedesign: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE');
            }],
            useRedesignCatalog: ['featureFlags', function (featureFlags) {
              return featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG');
            }],
            title: ['courseTrack', buildCourseTitle],
          },
        })
        .state('quiz', {
          url: '/tracks/:trackId/quiz',
          component: 'academyCourseTrackQuiz',
          parent: 'academy',
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', '$state', 'featureFlags', function (CourseTrackService, $transition$, $state, featureFlags) {
              return CourseTrackService.trackWithQuiz($transition$.params().trackId).then(res => {
                if (featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG') && res.data.learning_track_id) {
                  redirectToLearningTrackQuiz($state, 'learning-track-quiz', res.data);
                  return null;
                }
                return res.data;
              });
            }],
            user: UserAuthenticator,
            title: ['courseTrack', courseTrack => buildTrackTitle(courseTrack, 'Quiz')],
          },
        })
        .state('report-card', {
          url: '/tracks/:trackId/quiz/report-card',
          component: 'academyCourseTrackReportCard',
          parent: 'academy',
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', '$state', 'featureFlags', function (CourseTrackService, $transition$, $state, featureFlags) {
              return CourseTrackService.trackWithQuiz($transition$.params().trackId).then(res => {
                if (featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE_CATALOG') && res.data.learning_track_id) {
                  redirectToLearningTrackQuiz($state, 'learning-track-report-card', res.data);
                  return null;
                }
                return res.data;
              });
            }],
            user: UserAuthenticator,
            title: ['courseTrack', courseTrack => buildTrackTitle(courseTrack, 'Results')],
          },
        })
        .state('learning-track-quiz', {
          url: '/tracks/:learningTrackId/courses/:courseTrackId/quiz',
          component: 'academyCourseTrackQuiz',
          parent: 'academy',
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', function (CourseTrackService, $transition$) {
              return CourseTrackService.trackWithQuiz($transition$.params().courseTrackId).then(res => res.data);
            }],
            user: UserAuthenticator,
            title: ['courseTrack', courseTrack => buildTrackTitle(courseTrack, 'Quiz')],
          },
        })
        .state('learning-track-report-card', {
          url: '/tracks/:learningTrackId/courses/:courseTrackId/quiz/report-card',
          component: 'academyCourseTrackReportCard',
          parent: 'academy',
          resolve: {
            courseTrack: ['CourseTrackService', '$transition$', function (CourseTrackService, $transition$) {
              return CourseTrackService.trackWithQuiz($transition$.params().courseTrackId).then(res => res.data);
            }],
            user: UserAuthenticator,
            title: ['courseTrack', courseTrack => buildTrackTitle(courseTrack, 'Results')],
          },
        })
        .state('singlecourse', {
          url: '/:courseId',
          parent: 'courses',
          params: {
            sectionId: { squash: true, value: null },
          },
          resolve: {
            course: ['CourseService', '$state', '$transition$', 'featureFlags',
              function (CourseService, $state, $transition$, featureFlags) {
                return CourseService.find($transition$.params().courseId).then(res => {
                  const trackId = res.data?.course_track_id;
                  if (featureFlags.isOn('UTEST_ACADEMY_REDESIGN_COURSE') && trackId) {
                    $state.go('track-course', {
                      trackId,
                      courseId: $transition$.params().courseId,
                    });
                    return null;
                  }
                  return res;
                });
              }],
            user: UserResolver,
            title: ['course', course => (course ? `uTest Academy Course - ${course.data.title}` : '')],
          },
          views: {
            'courses-show': {
              template: singleCourseTemplate,
              controller: 'SingleCourseCtrl',
            },
          },
          sticky: true,
        });
    }]);
  },
};

export default CoursesRouter;
